import React, { useEffect, useState } from 'react';
import AdminLayout from '../UI/Layout/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faCalendarAlt, faChevronLeft, faChevronRight,
    faCreditCard, faUniversity,
} from '@fortawesome/free-solid-svg-icons';
import Axios from '../../axios/Axios';
import { userloginDetails } from '../Utils/AdminAuth';

const TRANS_SUMMARY_URL = 'https://napi.authkey.io/api2/v1/admin/trans_summary';

const toInputDate = (d) => d.toISOString().split('T')[0];

const today = new Date();

const GATEWAY_TYPES = [
    { id: 1, label: 'Razorpay', key: 'type1', icon: faCreditCard, color: '#4f46e5', light: '#eef2ff' },
    { id: 2, label: 'Stripe', key: 'type2', icon: faUniversity, color: '#0891b2', light: '#ecfeff' },
];


const emptyState = () => ({ data: [], currentPage: 1, totalPages: 1, totalRecords: 0, searched: false });

const PaymentGatewayList = () => {
    const lStorage = userloginDetails();
    const userId = lStorage?.user?.userId;
    const userToken = lStorage?.token;

    const [fromDate, setFromDate] = useState(toInputDate(today));
    const [toDate, setToDate] = useState(toInputDate(today));
    const [activeTab, setActiveTab] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState({ status: false, bgColor: '', message: '' });
    const [rpAmount, setRpAmount] = useState(0);
    const [stripeAmountByCurrency, setStripeAmountByCurrency] = useState({});
    const [usernameSearch, setUsernameSearch] = useState('');

    const [gatewayData, setGatewayData] = useState({
        type1: emptyState(),
        type2: emptyState(),
    });

    const showError = (msg, type = 'danger') => {
        setError({ status: true, bgColor: `alert alert-${type}`, message: msg });
        setTimeout(() => setError({ status: false, bgColor: '', message: '' }), 3500);
    };

    useEffect(() => {
        handleSearch();
    } ,[])

    const handleSearch = async (page = 1) => {
        if (!fromDate || !toDate) return showError('Please select both dates.');
        if (new Date(fromDate) > new Date(toDate)) return showError('From date cannot be after To date.');

        setLoading(true);
        setSearched(true);
        setGatewayData({ type1: emptyState(), type2: emptyState() });

        try {
            const { data } = await Axios.post(TRANS_SUMMARY_URL, {
                user_id: userId,
                token: userToken,
                method: 'get_transaction_details',
                from_date: fromDate,
                to_date: toDate,
                page,
            });

            if (data.success) {
                const rpRecords     = data.data_rp     || [];
                const stripeRecords = data.data_stripe || [];
                let totalrpAmount = 0;
                const stripeByCurrency = {};

                rpRecords.forEach((x) => {
                    totalrpAmount += Number(x.amount) || 0;
                });

                stripeRecords.forEach((x) => {
                    const cur = x.country_currency || 'Unknown';
                    stripeByCurrency[cur] = (stripeByCurrency[cur] || 0) + (Number(x.amount) || 0);
                });

                setRpAmount(totalrpAmount);
                setStripeAmountByCurrency(stripeByCurrency);

                setGatewayData({
                    type1: {
                        data: rpRecords,
                        currentPage: data.pagination?.current_page || page,
                        totalPages: data.pagination?.total_pages || 1,
                        totalRecords: rpRecords.length,
                        searched: true,
                    },
                    type2: {
                        data: stripeRecords,
                        currentPage: data.pagination?.current_page || page,
                        totalPages: data.pagination?.total_pages || 1,
                        totalRecords: stripeRecords.length,
                        searched: true,
                    },
                });
                
                
            } else {
                showError(data.message || 'Failed to fetch data.');
                setGatewayData({
                    type1: { ...emptyState(), searched: true },
                    type2: { ...emptyState(), searched: true },
                });
            }
        } catch (err) {
            showError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        const total = gatewayData.type1.totalPages;
        if (page < 1 || page > total) return;
        handleSearch(page);
    };

    const renderPagination = () => {
        const { currentPage, totalPages, totalRecords } = gatewayData.type1;
        if (totalPages <= 1) return null;

        const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
            }, []);

        return (
            <div style={{
                padding: '14px 24px', borderTop: '1px solid #f0f0f8',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
            }}>
                <span style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                    Page <span style={{ color: '#4f46e5' }}>{currentPage}</span> of <span style={{ color: '#4f46e5' }}>{totalPages}</span>
                    {totalRecords > 0 && <span style={{ color: '#64748b', fontWeight: '400' }}> ({totalRecords.toLocaleString()} records)</span>}
                </span>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            height: '32px', padding: '0 12px', borderRadius: '8px',
                            border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                            background: currentPage === 1 ? '#f8fafc' : '#fff',
                            color: currentPage === 1 ? '#cbd5e1' : '#4f46e5',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '10px' }} /> Prev
                    </button>

                    {pages.map((p, idx) =>
                        p === '...' ? (
                            <span key={'e' + idx} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '13px' }}>…</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => handlePageChange(p)}
                                style={{
                                    minWidth: '32px', height: '32px', borderRadius: '8px', fontSize: '12px',
                                    border: '1px solid', fontWeight: currentPage === p ? '700' : '400',
                                    borderColor: currentPage === p ? '#4f46e5' : '#e2e8f0',
                                    background: currentPage === p ? '#4f46e5' : '#fff',
                                    color: currentPage === p ? '#fff' : '#475569',
                                    cursor: 'pointer',
                                }}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        style={{
                            height: '32px', padding: '0 12px', borderRadius: '8px',
                            border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                            background: currentPage >= totalPages ? '#f8fafc' : '#fff',
                            color: currentPage >= totalPages ? '#cbd5e1' : '#4f46e5',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                    >
                        Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '10px' }} />
                    </button>
                </div>
            </div>
        );
    };

    const renderTable = () => {
        const key = activeTab === 1 ? 'type1' : 'type2';
        const allData = gatewayData[key].data;
        const data = usernameSearch.trim()
            ? allData.filter((row) => (row.username || '').toLowerCase().includes(usernameSearch.trim().toLowerCase()))
            : allData;

        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                    <span className="spinner-border spinner-border-sm mr-2" /> Loading...
                </div>
            );
        }

        if (data.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '14px' }}>
                    {!searched
                        ? 'Use the filters above and click Search.'
                        : usernameSearch.trim()
                            ? `No records found for username "${usernameSearch.trim()}".`
                            : 'No records found for this gateway.'}
                </div>
            );
        }


        return (
            <>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                        <thead>
                            <tr style={{ background: '#2d2d4e' }}>
                                {['Username', 'Transaction ID', 'Amount', 'Currency', 'Date'].map((h, i) => (
                                    <th key={i} style={{
                                        padding: '13px 16px', fontSize: '11px', fontWeight: '700',
                                        color: '#e2e2f0', textAlign: 'left', whiteSpace: 'nowrap',
                                        letterSpacing: '0.4px', textTransform: 'uppercase',
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, i) => (
                                <tr key={i} style={{
                                    background: i % 2 === 0 ? '#f7f7fc' : '#fff',
                                    borderBottom: '1px solid #ebebf5',
                                }}>
                                    {/* <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8', fontWeight: '500', width: '48px' }}>
                                        {i + 1}
                                    </td> */}
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{ fontSize: '13px', color: '#334155', fontWeight: '500' }}>
                                            {row.username || '-'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{
                                            fontSize: '12px', color: '#1e293b', fontWeight: '600',
                                            fontFamily: 'Consolas, monospace',
                                            background: '#f0f0f8', padding: '3px 8px',
                                            borderRadius: '6px', display: 'inline-block',
                                        }}>
                                            {row.transaction_id || '-'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{ fontSize: '14px', color: '#0f172a', fontWeight: '700' }}>
                                            {row.amount != null ? Number(row.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '-'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{
                                            fontSize: '11px', fontWeight: '700', padding: '3px 10px',
                                            borderRadius: '20px',
                                            background: row.country_currency === 'INR' ? '#f0fdf4' : '#eff6ff',
                                            color:      row.country_currency === 'INR' ? '#16a34a' : '#2563eb',
                                            border:     `1px solid ${row.country_currency === 'INR' ? '#bbf7d0' : '#bfdbfe'}`,
                                        }}>
                                            {row.country_currency || '-'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>
                                        {row.created || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {renderPagination()}
            </>
        );
    };

    const activeGW = GATEWAY_TYPES.find(g => g.id === activeTab);

    return (
        <AdminLayout>
            <main className="content">

                {/* Page Heading */}
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '20px' }}>
                        Payment Gateway Transactions
                    </h4>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                        View and filter transactions across all payment gateway types.
                    </p>
                </div>

                {/* Filter Card */}
                <div className="card" style={{ borderRadius: '12px', border: '1px solid #e8e8f0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                    <div className="card-body">
                        <h6 style={{ fontWeight: '700', color: '#1a1a2e', marginBottom: '16px', fontSize: '14px' }}>
                            Filter Transactions
                        </h6>

                        {error.status && (
                            <div className={error.bgColor} role="alert" style={{ marginBottom: '12px' }}>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => setError({ status: false })}
                                    aria-label="Close"
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                {error.message}
                            </div>
                        )}

                        <div className="row g-2 align-items-end">
                            <div className="col-12 col-sm-6 col-md-4">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    From Date
                                </label>
                                <div className="input-group">
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6b7280' }} />
                                    </div>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fromDate}
                                        max={toDate || toInputDate(today)}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                                <label className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                    To Date
                                </label>
                                <div className="input-group">
                                    <div className="input-group-text">
                                        <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6b7280' }} />
                                    </div>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={toDate}
                                        min={fromDate}
                                        max={toInputDate(today)}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-2">
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => handleSearch(1)}
                                    disabled={loading}
                                    style={{ fontWeight: '600', fontSize: '13px' }}
                                >
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSearch} className="mr-2" />
                                            Search
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab + Table Card */}
                <div style={{
                    background: '#fff', borderRadius: '12px',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                    border: '1px solid #e8e8f0',
                    marginTop: '20px', overflow: 'hidden',
                    fontFamily: 'Inter, system-ui, sans-serif',
                }}>
                    {/* Tabs */}
                    <div style={{
                        display: 'flex', borderBottom: '2px solid #f0f0f8',
                        background: '#fafafa',
                    }}>
                        {GATEWAY_TYPES.map((gw) => {
                            const isActive = activeTab === gw.id;
                            const key = gw.id === 1 ? 'type1' : 'type2';
                            const count = gatewayData[key].totalRecords;
                            return (
                                <button
                                    key={gw.id}
                                    onClick={() => setActiveTab(gw.id)}
                                    style={{
                                        flex: 1, padding: '14px 20px',
                                        border: 'none', cursor: 'pointer',
                                        borderBottom: isActive ? `2px solid ${gw.color}` : '2px solid transparent',
                                        marginBottom: '-2px',
                                        background: isActive ? '#fff' : 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <span style={{
                                        width: '30px', height: '30px', borderRadius: '8px',
                                        background: isActive ? gw.light : '#f1f5f9',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <FontAwesomeIcon icon={gw.icon} style={{ fontSize: '13px', color: isActive ? gw.color : '#94a3b8' }} />
                                    </span>
                                    <span style={{ fontSize: '13px', fontWeight: isActive ? '700' : '500', color: isActive ? gw.color : '#64748b' }}>
                                        {gw.label}
                                    </span>
                                    {count > 0 && (
                                        <span style={{
                                            fontSize: '10px', fontWeight: '700',
                                            background: isActive ? gw.color : '#e2e8f0',
                                            color: isActive ? '#fff' : '#64748b',
                                            borderRadius: '20px', padding: '2px 7px',
                                        }}>
                                            {count.toLocaleString()}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active Tab Header */}
                    <div style={{
                        padding: '14px 24px', borderBottom: '1px solid #f0f0f8',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                            width: '34px', height: '34px', borderRadius: '8px',
                            background: activeGW.light,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon={activeGW.icon} style={{ color: activeGW.color, fontSize: '14px' }} />
                        </span>
                        <div>
                            <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>
                                {activeGW.label}
                            </h6>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {gatewayData[activeGW.key].totalRecords > 0 ? (
                                    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                        <span>{gatewayData[activeGW.key].totalRecords.toLocaleString()} total records</span>
                                        <span style={{ color: '#cbd5e1' }}>|</span>
                                        {activeTab === 1 ? (
                                            <>
                                                <span>Total Amount:</span>
                                                <span style={{ color: activeGW.color, fontWeight: '700', fontSize: '12px' }}>
                                                    {rpAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} INR
                                                </span>
                                            </>
                                        ) : (
                                            Object.entries(stripeAmountByCurrency).length > 0 ? (
                                                Object.entries(stripeAmountByCurrency).map(([cur, amt], idx) => (
                                                    <React.Fragment key={cur}>
                                                        {idx > 0 && <span style={{ color: '#cbd5e1' }}>|</span>}
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            background: activeGW.light, borderRadius: '6px',
                                                            padding: '2px 8px', border: `1px solid ${activeGW.color}33`,
                                                        }}>
                                                            <span style={{ color: '#64748b', fontSize: '10px', fontWeight: '600' }}>{cur}</span>
                                                            <span style={{ color: activeGW.color, fontWeight: '700', fontSize: '12px' }}>
                                                                {amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </span>
                                                        </span>
                                                    </React.Fragment>
                                                ))
                                            ) : (
                                                <span>No currency data</span>
                                            )
                                        )}
                                    </span>
                                ) : 'No data loaded'}
                            </span>
                        </div>
                        </div>

                        <div className="input-group" style={{ maxWidth: '260px' }}>
                            <div className="input-group-text">
                                <FontAwesomeIcon icon={faSearch} style={{ color: '#6b7280', fontSize: '12px' }} />
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by username..."
                                value={usernameSearch}
                                onChange={(e) => setUsernameSearch(e.target.value)}
                                style={{ fontSize: '13px' }}
                            />
                        </div>
                    </div>

                    {/* Table Body */}
                    {renderTable()}
                </div>

            </main>
        </AdminLayout>
    );
};

export default PaymentGatewayList;
